import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnyAdapter } from '@drl/core/api/adapters/any-adapter.service';
import { CountAdapter } from '@drl/core/api/adapters/count-adapter.service';
import { ApiService } from '@drl/core/api/api.service';
import { CurrentUserAdapter } from '@drl/core/auth/adapters/current-user-adapter.service';
import { User } from '@drl/core/auth/models/user.model';
import { OnboardTypes, RoleType, UserStatus } from '@drl/core/enums';
import { Count } from '@drl/core/models';
import { Success } from '@drl/core/models/success.model';
import { StoreKeys } from '@drl/core/store/store-keys.enum';
import { SystemStoreFacadeService } from '@drl/core/store/system-store-facade.service';
import {
  CaregiverAdapter,
  CareGroupWithMembersAdapter,
  PatientAdapter,
  PatientCaregiverAdapter,
} from '@drl/shared/adapters';
import {
  AddActivityLogCommand,
  FetchSideEffectLogsCommand,
  GetActivityLogsCommand,
  GetCaregroupCommand,
  GetLeadsDashboardCount,
  GetLeadsFilterStatusCount,
  GetLeadsList,
  GetPatientCGExternalCommand,
  GetPatientMoodLogsCommand,
  GetTotalLeadsCount,
  GetUserCommand,
  GetUsersCommand,
} from '@drl/shared/commands';
import {
  ActivityLogs,
  Caregiver,
  CareGroup,
  CareGroupWithMembers,
  LeadsDashBoardCount,
  MoodLogs,
  Patient,
  TenantConfig
} from '@drl/shared/models';
import { PatientCaregiver } from '@drl/shared/models/patient-caregiver.model';
import { InMemoryStorageService } from 'ngx-webstorage-service';
import { cloneDeep } from 'lodash-es';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HealthTasksAdapter } from './shared/adapters/health-tasks-adapter.service';
import { MoodLogAdapter } from './shared/adapters/mood-log-adapter.service';
import { PatientPhrAdapter } from './shared/adapters/patient-phr.adapter';
import { PatientCGEhrAdapter } from './shared/adapters/patientcg-ehr.adapter';
import { PhrCategoriesAdapter } from './shared/adapters/phr-categories-adapter.service';
import { PhrTagsAdapter } from './shared/adapters/phr-tags-adapter.service';
import { TherapyPlanAdapter } from './shared/adapters/therapy-plan-adapter.service';
import {
  AddCaregiverCommand,
  AddHealthTaskCommand,
  AddPatientCommand,
  DeactivatePatientCommand,
  DelPatientHealthTaskCommand,
  FetchCaregroupCommand,
  FetchPatientFromEhr,
  FetchSinglePhr,
  FetchSpecialistCommand,
  GetHealthTasksCommand,
  GetPatientCGCommand,
  GetPatientHealthTasksCommand,
  GetPatientPhrsCommand,
  GetPatientsCommand,
  GetPatientsCountCommand,
  GetPatientTherapyPlansCommand,
  GetPhrCategoriesCommand,
  GetPhrTagsCommand,
  SendRecordsCommand,
  UpdateCaregiverCommand,
  UpdateHealthTaskCommand,
  UpdatePatientCommand,
} from './shared/commands';
import { TreatmentType } from './shared/enums/treatment-types.enum';
import {
  HealthTasks,
  PatientHealthTasks,
  PatientPhr,
  PhrCategory,
  PhrTags,
  SideEffectLogs,
  TherapyPlan,
  PatientCaregiver as PatientCG,
} from './shared/models';
import { GetCareGroupsCommand } from '@drl/shared/commands/get-care-groups.command';
import { GetCareGroupsSpecialistCommand } from '@drl/shared/commands/get-care-groups-specialist.command';
import { GetPatientOtp } from '@drl/shared/commands/get-otp-patient.command';
import { DownloadPatientListCommand } from './shared/commands/download-patient-list.command';
import { is } from 'date-fns/locale';
import { FetchTenantConfigCommand } from '@drl/core/store/commands';
import { LeadFilterModel, LeadsFilterCount, LeadTableData } from '../care-central/models/filters.model';

const sortOrderDesc = 'createdOn DESC';
const patientCount = 10;

@Injectable()
export class PatientFacadeService {
  

  constructor(
    private readonly apiService: ApiService,
    private readonly anyAdapter: AnyAdapter,
    private readonly moodLogAdapter: MoodLogAdapter,
    private readonly patientCgAdapter: PatientCaregiverAdapter,
    private readonly patientPhrAdapter: PatientPhrAdapter,
    private readonly countAdapter: CountAdapter,
    private readonly userAdapter: CurrentUserAdapter,
    private readonly systemStoreFacade: SystemStoreFacadeService,
    private readonly patientCGEhrAdapter: PatientCGEhrAdapter,
    private readonly inMemoryStore: InMemoryStorageService,
    private readonly phrCategoriesAdapter: PhrCategoriesAdapter,
    private readonly phrTagsAdapter: PhrTagsAdapter,
    private readonly healthTasksAdapter: HealthTasksAdapter,
    private readonly therapyPlanAdapter: TherapyPlanAdapter,
    private readonly careGpWithMembersAdapter: CareGroupWithMembersAdapter,
    private readonly patientAdapter: PatientAdapter,
    private readonly caregiverAdapter: CaregiverAdapter,
  ) { }

  getPatients(
    limit = patientCount,
    offset = 0,
    sortField?: string,
    sortOrder?: number,
    ...rest
  ): Observable<User[]> {
    const command: GetPatientsCommand<User> = new GetPatientsCommand(
      this.apiService,
      this.userAdapter,
    );
    let order = ["createdOn DESC"];
    if (sortField) {
      order = [`${sortField} ${sortOrder === 1 ? 'DESC' : 'ASC'}`];
    }



    const [roleIds, cgRoleId, name, contigoId, phone, status, isSelfOnb, isMinor] = rest;
    const queryData = {
      and: undefined,
      or: undefined,
      roleId: { inq: roleIds },
      contigoId: undefined,
      phone: undefined,
      status: undefined,
      isSelfOnb: undefined,
      isMinor: undefined
    };
    if (sortField && sortOrder === 1) {
      order = [`${sortField} DESC`];
    } else if (sortField && sortOrder === -1) {
      order = [`${sortField} ASC`];
    } else {
      // Do nothing
    }

    if (name) {
      const namesForQuery = name.split(" ", 3).filter(n => n && n.trim().length > 0);

      const orConditions = [];
      const fields = ['firstName', 'middleName', 'lastName'];

      fields.forEach(field => {
        namesForQuery.forEach(n => {
          orConditions.push({ [field]: { ilike: `${n}%` } });
        });
      });

      const or = orConditions.length > 0 ? orConditions : undefined;
      if (or) {
        queryData.and = [{ or: or }];
      }
    }


    if (contigoId) {
      queryData.contigoId = {
        ilike: `%${contigoId}%`,
      };
    }

    if (status && status.length > 0) {
      queryData.status = {
        inq: status,
      };
    }else{
      
      queryData.status = {
        nin: [UserStatus.REQUESTACESS,UserStatus.REQUEST_REJECTED],
      };
    }

    if (isSelfOnb !== undefined) {
      queryData.isSelfOnb = isSelfOnb;
    }
    if (isMinor !== undefined) {
      queryData.isMinor = isMinor;
    }


    const phoneQuery = { or: [],and:[] }

    if (phone) {
      const phoneFilter = {
        phone: { ilike: `%${phone}%` },
        roleId: { inq: roleIds.concat(cgRoleId) },
        isMinor: isMinor,
        status: undefined
      };

      if(status && status.length > 0) {
        phoneFilter.status = {
          inq: status,
        };
      }else{
        phoneFilter.status = {
          nin: [UserStatus.REQUESTACESS,UserStatus.REQUEST_REJECTED],
        };
      }

      // Start orConditions if not already defined
      if (!phoneQuery.or) {
        phoneQuery.or = [];
      }

      // Add phone filter as one OR condition
      phoneQuery.or.push(phoneFilter);
      
      // Remove direct phone filter outside 'or' because we put phone filter inside or now
      delete queryData.phone;
    }


    const queryStr = JSON.stringify({
      where: phone ? phoneQuery : queryData,
      order,
      limit,
      offset,
    });

    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };
    return forkJoin([
      this.systemStoreFacade.getRoles(),
      command.execute(),
    ]).pipe(
      map(data => {
        data[1].forEach(user => {
          user.role = data[0].find(role => role.id === user.roleId);
        });
        return data[1];
      }),
    );
  }


  getPatientPhrs(
    limit,
    offset = 0,
    patientId?: number,
    sortField?: string,
    sortOrder?: number,
    ...rest
  ): Observable<PatientPhr[]> {
    const command: GetPatientPhrsCommand<PatientPhr> =
      new GetPatientPhrsCommand(this.apiService, this.patientPhrAdapter);
    let order = [sortOrderDesc];
    const [title, phrId] = rest;
    const queryData = {
      title: undefined,
      id: undefined,
      patientId: undefined,
    };

    if (title) {
      queryData.title = {
        ilike: `${title}%`,
      };
    }

    if (phrId) {
      queryData.id = phrId;
    }

    if (patientId) {
      queryData.patientId = patientId;
    }

    if (sortField !== null && sortOrder === 1) {
      order = [`${sortField} DESC`];
    } else if (sortField !== null && sortOrder === -1) {
      order = [`${sortField} ASC`];
    } else {
      // Do nothing
    }

    const queryStr = JSON.stringify({
      where: queryData,
      order,
      limit,
      offset,
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };
    const categoryIndex = 2;
    const tagsIndex = 1;
    return forkJoin([
      this.getCategories(),
      this.getPhrTags(),
      command.execute(),
    ]).pipe(
      map(data => {
        data[categoryIndex].forEach(patientPhr => {
          patientPhr.category = data[0].find(
            category => category.id === patientPhr.categoryId,
          );

          if (patientPhr.tags) {
            patientPhr.phrTags = patientPhr.tags.map(id => {
              const tag: PhrTags = data[tagsIndex].find(
                tagname => id === tagname.id,
              );
              return tag && tag.name;
            });
          }
        });
        return data[categoryIndex];
      }),
    );
  }

  getSinglePhr(id: number) {
    const command: FetchSinglePhr<PatientPhr> = new FetchSinglePhr(
      this.apiService,
      this.patientPhrAdapter,
      id,
    );

    return command.execute();
  }

  getPatientCGDetails(id: number) {
    const command: GetPatientCGCommand<PatientCaregiver> =
      new GetPatientCGCommand(this.apiService, this.patientCgAdapter, id);

    return command.execute();
  }

  getPatientCGExternalDetails(id: string) {
    const command: GetPatientCGExternalCommand<PatientCaregiver> =
      new GetPatientCGExternalCommand(
        this.apiService,
        this.patientCgAdapter,
        id,
      );

    return command.execute();
  }

  getPatientsCount(
    sortField?: string,
    sortOrder?: number,
    ...rest
  ): Observable<Count> {
    const command: GetPatientsCountCommand<Count> = new GetPatientsCountCommand(
      this.apiService,
      this.countAdapter,
    );
    const [fName, lName, roleId, extId, email, phone] = rest;
    const queryData = {
      firstName: undefined,
      lastName: undefined,
      externalId: undefined,
      roleId: undefined,
      email: undefined,
      phone: undefined,
    };
    if (fName) {
      queryData.firstName = {
        ilike: `%${fName}%`,
      };
    }
    if (lName) {
      queryData.lastName = {
        ilike: `%${lName}%`,
      };
    }
    if (extId) {
      queryData.externalId = {
        ilike: `%${extId}%`,
      };
    }
    if (roleId) {
      queryData.roleId = roleId;
    }
    if (email) {
      queryData.email = {
        ilike: `%${email}%`,
      };
    }
    if (phone) {
      queryData.phone = {
        ilike: `%${phone}%`,
      };
    }

    const queryStr = JSON.stringify(queryData);
    command.parameters = {
      query: new HttpParams().set('where', queryStr),
    };
    return command.execute();
  }

  addPatient(patientCG): Observable<PatientCaregiver> {
    const command: AddPatientCommand<PatientCaregiver> = new AddPatientCommand(
      this.apiService,
      this.patientCgAdapter,
    );
    command.parameters = {
      data: patientCG,
    };
    return command.execute();
  }

  deactivatePatient(patientId,reason:string): Observable<{ status: string; }> {
    const command: DeactivatePatientCommand<{
      status: string;
      deactivationReason?: string;
    }> = new DeactivatePatientCommand(
      this.apiService,
      this.anyAdapter,
      patientId,
    );
    command.parameters = {
      data: {
        status: UserStatus.DEACTIVATED.toString(),
        deactivationReason: reason
      },
    };
    return command.execute();
  }

  sendRecordsToPatient(patientId: number, email: string): Observable<Success> {
    const command: SendRecordsCommand<any> = new SendRecordsCommand(
      this.apiService,
      this.anyAdapter,
      patientId,
    );
    command.parameters = {
      data: {
        email,
      },
    };
    return command.execute();
  }

  fetchCaregroups(id: number) {
    const command: FetchCaregroupCommand<CareGroup> = new FetchCaregroupCommand(
      this.apiService,
      this.anyAdapter,
      id,
    );

    return command.execute();
  }

  updatePatient(patientCg: {
    patient: Patient;
    caregiver: Caregiver;
  }): Observable<(Patient | Caregiver)[]> {
  
    const updatePatientCmd = new UpdatePatientCommand(
      this.apiService,
      this.patientAdapter,
      patientCg.patient.id
    );
  
    updatePatientCmd.parameters = {
      data: patientCg.patient,
    };
  
    return updatePatientCmd.execute().pipe(
      switchMap((updatedPatient: Patient) => {
  
        // If no caregiver → only return patient
        if (!patientCg.caregiver) {
          return of([updatedPatient]);
        }
  
        let caregiverCommand: Observable<Caregiver>;
  
        // Update existing caregiver
        if (patientCg.caregiver.id) {
          const updateCG = new UpdateCaregiverCommand(
            this.apiService,
            this.caregiverAdapter,
            patientCg.caregiver.id
          );
          updateCG.parameters = { data: patientCg.caregiver };
          caregiverCommand = updateCG.execute();
        }
  
        // Add new caregiver
        else {
          const addCG = new AddCaregiverCommand(
            this.apiService,
            this.caregiverAdapter,
            patientCg.patient.externalId
          );
          addCG.parameters = { data: patientCg.caregiver };
          caregiverCommand = addCG.execute();
        }
  
        // Return both results
        return caregiverCommand.pipe(
          map((updatedCaregiver: Caregiver) => [updatedPatient, updatedCaregiver])
        );
      })
    );
  }
  

  getSpecialists(): Observable<User[]> {
    return this.systemStoreFacade.getRoles().pipe(
      map(roleList => 
        roleList
          .filter(role => role.roleType === RoleType.Specialist)
          .map(role => role.id)
      ),
      switchMap(roleIds => {
        const command: FetchSpecialistCommand<User> = new FetchSpecialistCommand(
          this.apiService,
          this.userAdapter,
        );
  
        const queryData = {
          roleId: {
            inq: roleIds,
          },
        };
  
        const queryStr = JSON.stringify({
          where: queryData,
        });
  
        command.parameters = {
          query: new HttpParams().set('filter', queryStr),
        };
  
        return command.execute();
      }),
    );
  }
  

  getSpecialistById(userId: number) {
    const command: GetUserCommand<User> = new GetUserCommand(
      this.apiService,
      this.userAdapter,
      userId,
    );
    return command.execute();
  }

  fetchpatientDetails(extId: string): Observable<PatientCG[]> {
    const command: FetchPatientFromEhr<PatientCG> = new FetchPatientFromEhr(
      this.apiService,
      this.patientCGEhrAdapter,
      extId,
    );

    return command.execute();
  }

  getCategories(reset = false): Observable<PhrCategory[]> {
    const phrCategoriesInStore = this.inMemoryStore.get(
      StoreKeys.PHR_CATEGORIES,
    );
    if (!reset && phrCategoriesInStore) {
      return of(cloneDeep(phrCategoriesInStore));
    } else {
      const command: GetPhrCategoriesCommand = new GetPhrCategoriesCommand(
        this.apiService,
        this.phrCategoriesAdapter,
      );
      return command.execute().pipe(
        tap(phrCategories => {
          this.inMemoryStore.set(
            StoreKeys.PHR_CATEGORIES,
            cloneDeep(phrCategories),
          );
        }),
      );
    }
  }

  getPhrTags(reset = false): Observable<PhrTags[]> {
    const phrTagsInStore = this.inMemoryStore.get(StoreKeys.PHR_TAGS);
    if (!reset && phrTagsInStore) {
      return of(cloneDeep(phrTagsInStore));
    } else {
      const command: GetPhrTagsCommand = new GetPhrTagsCommand(
        this.apiService,
        this.phrTagsAdapter,
      );
      return command.execute().pipe(
        tap(phrTags => {
          this.inMemoryStore.set(StoreKeys.PHR_TAGS, cloneDeep(phrTags));
        }),
      );
    }
  }

  getHealthTasks(reset = false): Observable<HealthTasks[]> {
    const healthTasksInStore = this.inMemoryStore.get(StoreKeys.HEALTH_TASKS);
    if (!reset && healthTasksInStore) {
      return of(cloneDeep(healthTasksInStore));
    } else {
      const command: GetHealthTasksCommand = new GetHealthTasksCommand(
        this.apiService,
        this.healthTasksAdapter,
      );
      return command.execute().pipe(
        tap(healthTasks => {
          this.inMemoryStore.set(
            StoreKeys.HEALTH_TASKS,
            cloneDeep(healthTasks),
          );
        }),
      );
    }
  }

  getPatientHealthTasks(
    limit,
    offset = 0,
    patientId?: number,
    sortField?: string,
    sortOrder?: number,
    ...rest
  ): Observable<PatientHealthTasks[]> {
    const command: GetPatientHealthTasksCommand<PatientHealthTasks> =
      new GetPatientHealthTasksCommand(
        this.apiService,
        this.anyAdapter,
        patientId,
      );
    let order = [sortOrderDesc];
    const [healthTasksId] = rest;
    const queryData = {
      healthTasksId: undefined,
      patientId: undefined,
    };

    if (healthTasksId) {
      queryData.healthTasksId = healthTasksId;
    }

    if (patientId) {
      queryData.patientId = patientId;
    }

    if (sortField && sortOrder === 1) {
      order = [`${sortField} DESC`];
    } else if (sortField && sortOrder === -1) {
      order = [`${sortField} ASC`];
    } else {
      // Do nothing
    }

    const queryStr = JSON.stringify({
      where: queryData,
      order,
      limit,
      offset,
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };

    return command.execute();
  }

  addPatientHealthTask(
    patHealthTask,
    patientId,
  ): Observable<PatientHealthTasks> {
    const command: AddHealthTaskCommand<PatientHealthTasks> =
      new AddHealthTaskCommand(this.apiService, this.anyAdapter, patientId);
    command.parameters = {
      data: patHealthTask,
    };
    return command.execute();
  }

  removePatientHealthTask(
    patientId: number,
    healthTaskId: number,
  ): Observable<void> {
    const command: DelPatientHealthTaskCommand<void> =
      new DelPatientHealthTaskCommand(
        this.apiService,
        this.anyAdapter,
        patientId,
      );

    const queryData = {
      healthTasksId: undefined,
    };

    if (healthTaskId) {
      queryData.healthTasksId = healthTaskId;
    }

    command.parameters = {
      query: new HttpParams().set('where', JSON.stringify(queryData)),
    };

    return command.execute();
  }

  updatePatientHealthTask(patHealthTask, patientId) {
    const command: UpdateHealthTaskCommand<HealthTasks> =
      new UpdateHealthTaskCommand(this.apiService, this.anyAdapter, patientId);
    const queryData = {
      healthTasksId: undefined,
    };
    if (patHealthTask.healthTasksId) {
      queryData.healthTasksId = patHealthTask.healthTasksId;
    }
    command.parameters = {
      data: patHealthTask,
      query: new HttpParams().set('where', JSON.stringify(queryData)),
    };
    return command.execute();
  }

  getSingleCaregroup(id: number) {
    const command: GetCaregroupCommand<CareGroupWithMembers> =
      new GetCaregroupCommand(
        this.apiService,
        this.careGpWithMembersAdapter,
        id,
      );

    return command.execute();
  }

  getPatientTherapyPlans(id: number, where?: { treatmentType?: TreatmentType; }) {
    const command: GetPatientTherapyPlansCommand<TherapyPlan> =
      new GetPatientTherapyPlansCommand(
        this.apiService,
        this.therapyPlanAdapter,
        id,
      );

    const order = [];
    const queryData = {
      treatmentType: undefined,
    };
    if (where && where.treatmentType) {
      queryData.treatmentType = where.treatmentType;
    }

    const queryStr = JSON.stringify({
      where: queryData,
      order,
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };
    return command.execute();
  }

  getSideEffectLogs(patientId: number, startDate?: Date, endDate?: Date) {
    const command: FetchSideEffectLogsCommand<SideEffectLogs> =
      new FetchSideEffectLogsCommand(this.apiService, this.anyAdapter);

    const sortOrderAsc = 'logDate ASC';

    const order = [sortOrderAsc];
    const queryData = {
      patientId: undefined,
      start_date: undefined,
      end_date: undefined,
    };

    if (patientId) {
      queryData.patientId = patientId;
    }

    if (startDate) {
      queryData.start_date = startDate;
    }

    if (endDate) {
      queryData.end_date = endDate;
    }

    const queryStr = JSON.stringify({
      where: queryData,
      order,
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };

    return command.execute();
  }

  getMoodLogs(patientId: number, startDate?: Date, endDate?: Date) {
    const command: GetPatientMoodLogsCommand<MoodLogs> =
      new GetPatientMoodLogsCommand(this.apiService, this.moodLogAdapter);

    const order = ['dateTime ASC'];
    const queryData = {
      patientId: undefined,
      start_date: undefined,
      end_date: undefined,
    };

    if (patientId) {
      queryData.patientId = patientId;
    }

    if (startDate) {
      queryData.start_date = startDate;
    }

    if (endDate) {
      queryData.end_date = endDate;
    }

    const queryStr = JSON.stringify({
      where: queryData,
      order,
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };

    return command.execute();
  }

  getCareGroupsCancerTypeSpecialists(cancerType: string, careGroupSpecific: boolean = true) {
    const command: GetCareGroupsSpecialistCommand<Partial<CareGroup>> =
      new GetCareGroupsCommand(this.apiService, this.anyAdapter);

    const queryData = {
      cancerType: { ilike: `%${cancerType}%` },
    };

    const queryStr = JSON.stringify({
      fields: {
        specialistId: true,
      },
      where: queryData,
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr).set('care-group-specific-disable', `${careGroupSpecific}`),
    };
    return command.execute();
  }

  sendOtp(userId: number, isMinor: boolean = false): Observable<Success> {
    const command: GetPatientOtp<Success> =
      new GetPatientOtp(this.apiService, this.anyAdapter, userId, isMinor);

    command.parameters = {
      query: new HttpParams().set('tenantId', this.inMemoryStore.get(StoreKeys.TENANT_ID)).set('onBrdType', OnboardTypes.HOSPITAL.toString()),
    };

    return command.execute();
  }

  downloadPtList() {
    const command: DownloadPatientListCommand<Success> =
      new DownloadPatientListCommand(this.apiService, this.anyAdapter);

    command.parameters = {
      data: null,
    };
    return command.execute();
  }

  fetchAllUsersByExtId(extIds: string[], roleIds?: Number[], isMinor?: boolean): Observable<User[]> {
    const command: GetPatientsCommand<User> = new GetPatientsCommand(
      this.apiService,
      this.userAdapter,
    );
    const queryData = {
      externalId: { inq: extIds },
      roleId: undefined,
      isMinor: undefined,
    };
    if (roleIds && roleIds.length > 0) {
      queryData.roleId = { inq: roleIds };
    }
    if (isMinor !== undefined) {
      queryData.isMinor = isMinor;
    }
    const queryStr = JSON.stringify({
      where: queryData
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr)
    };
    return command.execute();
  }

   getTenantConfig(configType): Observable<TenantConfig> | undefined {
      const command: FetchTenantConfigCommand<TenantConfig> = new FetchTenantConfigCommand(
        this.apiService,
        this.anyAdapter,
      );
  
      if (!configType) {
        return;
      }
  
      command.parameters = {
        query: new HttpParams().set('config_key', configType),
      };
  
      return command.execute();
    }

    getLeadDasboardCount() {
      const command: GetLeadsDashboardCount<LeadsDashBoardCount> =
      new GetLeadsDashboardCount(this.apiService, this.anyAdapter);
      return command.execute();
    }

    getLeadsFilterCount() {
      const command: GetLeadsFilterStatusCount<LeadsFilterCount> =
      new GetLeadsFilterStatusCount(this.apiService, this.anyAdapter);
      return command.execute();
    }

    getLeadsList(
      filters: LeadFilterModel, 
      offset = 0, 
      limit = 20, 
      search?: string, 
      sortField?: string, 
      sortOrder?: number
    ): Observable<LeadTableData[]> {
      
      const command: GetLeadsList<LeadTableData> = new GetLeadsList(this.apiService, this.anyAdapter);
    
      let queryParams = new HttpParams()
        .set('filters', JSON.stringify(filters)) // Adding filters
        .set('limit', limit.toString())          // Adding the limit
        .set('offset', offset.toString())        // Adding the offset
        .set('name', search ? search : '');      // Adding the search query, if any
      
      if (sortField &&sortOrder) {
        queryParams = queryParams.set('sortField', sortField);
        queryParams = queryParams.set('sortOrder', sortOrder.toString());
      }
      
    
      command.parameters = {
        query: queryParams
      };
    
      // Execute the command and return the observable result
      return command.execute();
    }
    

    getTotalLeadsCount(filters?: LeadFilterModel,search?:string): Observable<{count:number}> {
      const command: GetTotalLeadsCount<{count:number}> =
      new GetTotalLeadsCount(this.apiService, this.anyAdapter);    
      command.parameters = {
        query: new HttpParams()
          .set('filters', JSON.stringify(filters))
          .set('name', search ? search : '')
      }; 
      return command.execute();
    }

    createActivityLog(activityLog: ActivityLogs): Observable<ActivityLogs> {
      const command: AddActivityLogCommand<ActivityLogs> = new AddActivityLogCommand(
        this.apiService,
        this.anyAdapter,
      );
      command.parameters = {
        data: activityLog,
      };
      return command.execute();
    }

    getActivityLogs(patientId: number,activityTpe:string, subType?:string,offset = 0, limit = 10): Observable<ActivityLogs[]> {
      const command: GetActivityLogsCommand<ActivityLogs> =
        new GetActivityLogsCommand(this.apiService, this.anyAdapter);

      const order = ['createdOn DESC'];
      const queryData = {
        patientId: undefined,
        activityType: undefined,
        subType: undefined
      };

      if (patientId) {
        queryData.patientId = patientId;
      }
      if (activityTpe) {
        queryData.activityType = activityTpe;
      }
      if (subType) {
        queryData.subType = subType;
      }

      const queryStr = JSON.stringify({
        where: queryData,
        order,
        limit,
        offset,
      });
      command.parameters = {
        query: new HttpParams().set('filter', queryStr),
      };

      return command.execute();
    }

    fetchUserByPhone(phone: string,userId?:number): Observable<User[]> {
      const command: GetUsersCommand<User> = new GetUsersCommand(
        this.apiService,
        this.userAdapter,
      );
      phone = phone.replace(/[+\s]/g, ''); // Remove spaces from phone number
      const queryData = {
        phone: {ilike: `%${phone}%`},
        id: undefined
      };
      if(userId) {
        queryData.id = {neq: userId};
      }

      const queryStr = JSON.stringify({
        where: queryData,
      });
      command.parameters = {
        query: new HttpParams().set('filter', queryStr),
      };
      return command.execute();
    }

    getPrescriptionRefillCount() {
    }
}
